import { createSelector } from "reselect";
import { RootState } from "@/core/store";

// Basic state selectors
export const selectOrdersState = (state: RootState) => state.orders;
export const selectCurrentOrder = (state: RootState) =>
  state.orders.currentOrder;
export const selectOrders = (state: RootState) => state.orders.orders;
export const selectOrdersLoading = (state: RootState) => state.orders.loading;
export const selectOrdersError = (state: RootState) => state.orders.error;

// Cart-specific selectors
export const selectCartItems = createSelector(
  [selectCurrentOrder],
  (currentOrder) => currentOrder.items
);

export const selectCartTotal = createSelector(
  [selectCurrentOrder],
  (currentOrder) => currentOrder.sum
);

export const selectCartItemsCount = createSelector(
  [selectCartItems],
  (items) => items.length
);

export const selectCartComment = createSelector(
  [selectCurrentOrder],
  (currentOrder) => currentOrder.comment || ""
);

export const selectCartLoading = createSelector(
  [selectCurrentOrder],
  (currentOrder) => currentOrder.loading
);

export const selectCartError = createSelector(
  [selectCurrentOrder],
  (currentOrder) => currentOrder.error
);

// Cart statistics selector
export const selectCartStats = createSelector(
  [selectCartItems, selectCartTotal],
  (items, total) => ({
    itemCount: items.length,
    totalSum: total,
    isEmpty: items.length === 0,
    totalQuantity: items.reduce((sum, item) => sum + item.qty, 0),
    averageItemPrice:
      items.length > 0
        ? total / items.reduce((sum, item) => sum + item.qty, 0)
        : 0,
  })
);

// Legacy selectors (kept for backward compatibility)
export const selectCurrentOrderSum = selectCartTotal;
export const selectCurrentOrderItemsCount = selectCartItemsCount;

export const selectTotalOrdersSum = createSelector([selectOrders], (orders) =>
  orders.reduce((total, order) => total + order.sum, 0)
);

// Order history selectors
export const selectOrderHistory = createSelector(
  [selectOrdersState],
  (ordersState) => ordersState.orderHistory
);

export const selectOrderHistoryItems = createSelector(
  [selectOrderHistory],
  (orderHistory) => orderHistory.items
);

export const selectOrderHistoryLoading = createSelector(
  [selectOrderHistory],
  (orderHistory) => orderHistory.loading
);

export const selectOrderHistoryHasMore = createSelector(
  [selectOrderHistory],
  (orderHistory) => orderHistory.hasMore
);

export const selectOrderHistoryFilters = createSelector(
  [selectOrderHistory],
  (orderHistory) => orderHistory.filters
);
